import { AUTH_STORAGE } from './constants';
import { StorageData, StorageDataValue } from './types';

/**
 * Creates a Singleton chached storage in order to save and use user credentials and needed data,
 * working together with the browser session storage to save those values with need a longer cache time.
 */
export class Storage {

  /**
   * Gets the unique instance of Storage.
   * @returns {Storage}
   */
  public static getInstance(): Storage {
    if (!Storage.instance) {
      Storage.instance = new Storage();
    }

    return Storage.instance;
  }

  /**
   * Singleton Storage instance.
   */
  private static instance: Storage;

  /**
   * Contains the saved data into the Storage.
   */
  private data: StorageData;

  /**
   * Creates an instance and init the StoragedData.
   */
  private constructor() {
    if (typeof window.sessionStorage !== 'object' || !window.sessionStorage.length) {
      this.data = {};
      return;
    }

    const storedData = window.sessionStorage.getItem(AUTH_STORAGE);

    this.data = JSON.parse(storedData || '{}');
  }

  /**
   * Sets a value into the cached storage and into the browser session storage.
   * @param {string} key
   * @param {StorageDataValue} value
   */
  public set(key: string, value: StorageDataValue): void {
    this.data = {
      ...this.data,
      [key]: value
    };

    if (typeof window.sessionStorage === 'object') {
      window.sessionStorage.setItem(AUTH_STORAGE, JSON.stringify(this.data || null));
    }
  }

  public get<S>(key: string, defaultValue: S): S;

  /**
   * Gets a value from the cached storage.
   * @param {string} key
   * @param {StorageDataValue} defaultValue
   * @returns {StorageDataValue}
   */
  public get(key: string, defaultValue: StorageDataValue): StorageDataValue {
    const value = this.data[key];

    return typeof value !== 'undefined' ? value : defaultValue;
  }

  /**
   * Removes a value from the cached storage and udpates the global Session Storage without that value.
   * @param {string} key
   */
  public remove(key: string): void {
    delete this.data[key];

    if (typeof window.sessionStorage === 'object') {
      window.sessionStorage.setItem(AUTH_STORAGE, JSON.stringify(this.data || null));
    }

    if (!Object.keys(this.data).length) {
      this.clear();
    }
  }

  /**
   * Clears the chaced storage and clears the browser session storage.
   */
  public clear(): void {
    this.data = {};

    if (typeof window.sessionStorage === 'object') {
      window.sessionStorage.clear();
    }
  }
}
